import { type useAuth0 } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';

import { handleError } from './Error';
import { AppRoutes } from './routes';
import { SocketTypes, wsEnsureConnected } from './socketHelpers';

import { fmhAPI } from 'api/APIBase';
import { getFmhToken } from 'api/authAPI';
import { type AppDispatch } from 'store';
import { setAccessToken, setNpi } from 'store/auth/authSlice';

export const clearDataForLogout = (clearSessionStorage = true) => {
    if (clearSessionStorage) {
        sessionStorage.clear();
    }
    datadogRum.clearUser();
    pendo.clearSession?.(); // Optional chaining to avoid error if pendo is not initialized
};

export const getFMHTokenAndEnsureSocketConnections = async ({
    getAccessTokenSilently,
    logout,
    dispatch,
}: {
    getAccessTokenSilently: ReturnType<typeof useAuth0>['getAccessTokenSilently'];
    logout: ReturnType<typeof useAuth0>['logout'];
    dispatch: AppDispatch;
}) => {
    try {
        const fmhTokenResult = await getFmhToken({
            accessToken: (await getAccessTokenSilently())!,
        });

        const accessToken = fmhTokenResult.access_token;
        fmhAPI.base = fmhAPI.base.auth(`Bearer ${accessToken}`);

        dispatch(
            setAccessToken({
                accessToken,
                selectedNpi: fmhTokenResult.npi,
                permissions: fmhTokenResult.permissions,
                analyticsAccess: fmhTokenResult.analytics_access,
            }),
        );

        dispatch(
            setNpi({
                accessToken,
                selectedNpi: fmhTokenResult?.npi,
                permissions: fmhTokenResult?.permissions,
                analyticsAccess: fmhTokenResult.analytics_access,
            }),
        );

        dispatch(
            wsEnsureConnected({ socketType: SocketTypes.USER_SUPPORT, npi: fmhTokenResult.npi }),
        );
        return fmhTokenResult;
    } catch (err) {
        handleError('Error authenticating.', err as Error);
        clearDataForLogout();
        await logout({
            logoutParams: {
                returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
            },
        });
        return undefined;
    }
};
